/// <amd-module name="Feed/Legacy/Protocols/Maven/MavenDataService" />
import { IVssService, Services, VssService } from "VSS/Platform/Context";

import { Package } from "Feed/Common/Contracts/Feed";
import * as Contracts from "Feed/Common/Protocols/Maven/Contracts/Maven";
import { IMavenRestClient } from "Feed/Common/Protocols/Maven/RestClient/Maven";
import { UpstreamingBehavior } from "Feed/Common/Protocols/Shared/Contracts/PackagingShared";
import * as PackageResources from "Feed/Common/Resources";
import { BatchExecutor, IBatchIterator } from "Feed/Legacy/Protocols/Common/BatchHandling";
import { Feed, projId } from "Feed/Legacy/WebApi/VSS.CustomFeed.Contracts";

export interface IFeedMavenDataService extends IVssService {
    readonly batchSize: number;
    batchDeletePackageVersions(
        feed: Feed,
        iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>
    ): Promise<void>;
    batchRestoreToFeed(feed: Feed, iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>): Promise<void>;
    batchPermanentlyDelete(feed: Feed, iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>): Promise<void>;
    batchPromotePackages(
        feed: Feed,
        viewId: string,
        iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>
    ): Promise<void[]>;
    getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior>;
    setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void>;
}

class MavenDataService extends VssService implements IFeedMavenDataService {
    get MavenRestClient(): IMavenRestClient {
        return this.pageContext.getRestClient<IMavenRestClient>("IMavenRestClient");
    }

    get batchSize() {
        return 10;
    }

    public async batchDeletePackageVersions(
        feed: Feed,
        iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>
    ): Promise<void> {
        await BatchExecutor.executeInParallel(
            iterator,
            async (packageDetails: Contracts.MavenMinimalPackageDetails[]) => {
                const batchRequest = <Contracts.MavenPackagesBatchRequest>{
                    data: null,
                    operation: Contracts.MavenBatchOperationType.Delete,
                    packages: packageDetails
                };
                await this.MavenRestClient.updatePackageVersions(batchRequest, feed.id, projId(feed));
            }
        );
    }

    public async batchRestoreToFeed(
        feed: Feed,
        iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>
    ): Promise<void> {
        await BatchExecutor.executeInParallel(
            iterator,
            async (packageDetails: Contracts.MavenMinimalPackageDetails[]) => {
                const batchRequest = <Contracts.MavenPackagesBatchRequest>{
                    data: null,
                    operation: Contracts.MavenBatchOperationType.RestoreToFeed,
                    packages: packageDetails
                };
                await this.MavenRestClient.updateRecycleBinPackages(batchRequest, feed.id, projId(feed));
            }
        );
    }

    public async batchPermanentlyDelete(
        feed: Feed,
        iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>
    ): Promise<void> {
        await BatchExecutor.executeInParallel(
            iterator,
            async (packageDetails: Contracts.MavenMinimalPackageDetails[]) => {
                const batchRequest = <Contracts.MavenPackagesBatchRequest>{
                    data: null,
                    operation: Contracts.MavenBatchOperationType.PermanentDelete,
                    packages: packageDetails
                };
                await this.MavenRestClient.updateRecycleBinPackages(batchRequest, feed.id, projId(feed));
            }
        );
    }

    public batchPromotePackages(
        feed: Feed,
        viewId: string,
        iterator: IBatchIterator<Contracts.MavenMinimalPackageDetails[]>
    ): Promise<void[]> {
        return BatchExecutor.executeInParallel(iterator, async packageDetails => {
            const batchRequest = <Contracts.MavenPackagesBatchRequest>{
                data: { viewId },
                operation: Contracts.MavenBatchOperationType.Promote,
                packages: packageDetails
            };
            await this.MavenRestClient.updatePackageVersions(batchRequest, feed.id, projId(feed));
        });
    }

    public getUpstreamingBehavior(feed: Feed, pkg: Package): Promise<UpstreamingBehavior> {
        const nameParts = this.parseName(pkg.name);
        return this.MavenRestClient.getUpstreamingBehavior(feed.id, nameParts[0], nameParts[1], projId(feed));
    }

    public setUpstreamingBehavior(feed: Feed, pkg: Package, behavior: UpstreamingBehavior): Promise<void> {
        const nameParts = this.parseName(pkg.name);
        return this.MavenRestClient.setUpstreamingBehavior(feed.id, nameParts[0], nameParts[1], behavior, projId(feed));
    }

    private parseName(packageName: string): string[] {
        // Maven packages are of the form groupId:artifactId. Anything other than that are rejected.
        const nameParts = (packageName || "").split(":");
        if (nameParts.length !== 2) {
            throw new Error(PackageResources.MavenOverview_InvalidPackageError);
        }
        return nameParts;
    }
}

Services.add("IFeedMavenDataService", { serviceFactory: MavenDataService });
